/**
 * Maps API interview/self-schedule response to the wizard's internal payload shape.
 * Used for edit mode: fetch existing interview → populate form fields.
 */
import type { InterviewDetails } from '@/api/useInterviews';
import type { ScheduleInterviewPayload } from '../schedule.types';

/**
 * Map an existing interview from the API into the wizard payload.
 * Converts API field names to form field names.
 */
export function hydrateDetailsFromApi(
  interview: InterviewDetails
): Record<string, unknown> {
  const participants = interview.participants ?? [];
  const participantIds =
    interview.participant_ids ??
    participants.map((p) => p.id);

  return {
    interviewName: interview.name ?? '',
    interview_type: interview.kind ?? interview.interview_kind ?? 'one_on_one',
    participant_ids: participantIds.map(String),
    duration: interview.duration ?? 30,
    location: interview.location ?? 'zoom',
    host_id: interview.host_id ? String(interview.host_id) : '',
    ats_office_id: interview.ats_office_id
      ? String(interview.ats_office_id)
      : '',
    calendar_visibility:
      interview.calendar_visibility === 'private' ||
      interview.calendar_visibility === true,
    note_taker_enabled: Boolean(interview.note_taker_enabled),
    fill_with_ai_enabled: Boolean(interview.fill_with_ai_enabled),
    // Meeting rooms
    book_meeting_room:
      Array.isArray(interview.meeting_rooms) &&
      interview.meeting_rooms.length > 0,
    meeting_room_ids: (interview.meeting_rooms ?? []).map(
      (r) => r.email ?? ''
    ),
    // Booked slot (for manual mode)
    ...(interview.start_time && interview.end_time
      ? {
          bookedSlot: {
            start: interview.start_time,
            end: interview.end_time,
            duration: interview.duration ?? 30,
          },
        }
      : {}),
    // Participant default options for re-rendering selected chips
    participant_default_options: participants.map((p) => ({
      value: String(p.id),
      label: p.name ?? p.email ?? `User ${p.id}`,
      image: p.avatar_url,
    })),
    timezone: interview.timezone,
  };
}

/**
 * Build the full wizard payload from an existing interview.
 * Communication and scorecard are populated separately by their respective steps.
 */
export function hydratePayloadFromApi(
  interview: InterviewDetails
): ScheduleInterviewPayload {
  return {
    details: hydrateDetailsFromApi(interview),
    communication: {},
    scorecard: interview.scorecard_config ?? {},
  };
}
